import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useAuthStore } from './auth';
import { encrypt, decrypt } from '@/utils/secureStorage';
import { showToast } from '@/utils/swal';

export const useCartStore = defineStore('cart', () => {
    const auth = useAuthStore();
    const cartItems = ref([]);
    const productStates = ref({});

    const cartCount = computed(() =>
        cartItems.value.reduce((total, item) => total + item.quantity, 0)
    );

    const cartTotal = computed(() =>
        cartItems.value.reduce((total, item) => total + (Number(item.price * item.quantity)), 0)
    );

    const getUserKey = () => `cart_user_${auth.userinfo?.id}`;

    const initializeProductStates = (products) => {
        const state = {};
        for (const product of products) {
            state[product.id] = {
                name: product.name,
                description: product.description,
                price: product.price,
                productId: product.id,
                quantity: 1,
                languageId: null
            };
        }
        productStates.value = state;
    };

    const getQty = (productId) => productStates.value[productId]?.quantity ?? 1;

    const setQty = (productId, value) => {
        productStates.value[productId].quantity = value < 1 ? 1 : value;
    };

    const setLanguage = (productId, langId = null) => {
        productStates.value[productId].languageId = langId;
    };

    const addToCart = (productId) => {
        const state = productStates.value[productId];
        // if (!state || !state.languageId) {
        //     showToast({
        //         icon: 'error',
        //         title: 'Error',
        //         text: 'Language is required for product.',
        //     });

        //     return; // 🚫 Don't proceed without languageId
        // }

        const existingIndex = cartItems.value.findIndex(
            item => item.productId === productId && item.languageId === state.languageId
        );

        if (existingIndex !== -1) {
            cartItems.value[existingIndex].quantity += state.quantity;
        } else {
            cartItems.value.push({ ...state });
        }

        showToast({
            icon: 'success',
            title: 'Success',
            text: 'Product added to cart successfully.',
        });

        saveCartToStorage();
    };

    const saveCartToStorage = () => {
        if (!auth.userinfo?.id) return;
        const encrypted = encrypt(cartItems.value);
        localStorage.setItem(getUserKey(), encrypted);
    };

    const loadCartFromStorage = () => {
        if (!auth.userinfo?.id) return;
        const stored = localStorage.getItem(getUserKey());
        if (stored) {
            const decrypted = decrypt(stored);
            if (decrypted) {
                cartItems.value = decrypted;
            }
        }
    };

    const increaseCartQuantity = (productId, languageId = null) => {
        const item = cartItems.value.find(i => i.productId === productId && i.languageId === languageId);
        if (item) item.quantity++;

        saveCartToStorage();
    }

    const decreaseCartQuantity = (productId, languageId = null) => {
        const item = cartItems.value.find(i => i.productId === productId && i.languageId === languageId);
        if (item && item.quantity > 1) item.quantity--;

        saveCartToStorage();
    }

    const setCartQuantity = (productId, languageId = null, value) => {
        const item = cartItems.value.find(i => i.productId === productId && i.languageId === languageId);
        if (item) item.quantity = value < 1 ? 1 : value;

        saveCartToStorage();
    }

    const removeFromCart = (productId, languageId = null) => {
        cartItems.value = cartItems.value.filter(
            i => !(i.productId === productId && i.languageId === languageId)
        );

        saveCartToStorage();
    }

    const clearCart = () => {
        cartItems.value = [];
        localStorage.removeItem(getUserKey());
    };

    const clearCartLocalStorage = () => {
        for (const key in localStorage) {
            if (key.startsWith('cart_')) {
                localStorage.removeItem(key);
            }
        }

        cartItems.value = [];
    };

    // Sync cart on login or change
    watch(() => auth.userinfo?.id, (newId) => {
        if (newId) loadCartFromStorage();
    }, { immediate: true });

    return {
        cartItems,
        productStates,
        cartCount,
        cartTotal,
        initializeProductStates,
        getQty,
        setQty,
        setLanguage,
        addToCart,
        clearCart,
        increaseCartQuantity,
        decreaseCartQuantity,
        setCartQuantity,
        removeFromCart,
        clearCartLocalStorage
    };
});
